import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { ContentCopy, MoreVert } from "@mui/icons-material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { addRecentProject } from "../utils";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { setSelectedItem } from "@/features/bughurd/slice";
import CardPagination from "@/Components/Common/CardPagination";

type ProjectListViewProps = {
  projects: any;
  isLoading: boolean;
  isFetching: boolean;
  boardTypeFilter: string;
  paginationModel: {
    pageSize: number;
    page: number;
  };
  setPaginationModel: (model: any) => void;
};

export default function ProjectListView({
  projects,
  isLoading,
  isFetching,
  boardTypeFilter,
  paginationModel,
  setPaginationModel,
}: ProjectListViewProps) {
  const { roleCodeDetails: myPermissions, dateFormat } = useAppSelector(
    (state) => state.appGlobal,
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["bugherd"]);
  const baseUrl = window?.location?.origin;
  const isPmoHub = baseUrl?.includes("pmohub") || false;
  const [cardMenuAnchor, setCardMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [selectedProject, setSelectedProject] = useState<any>(null);

  const handleCardMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    project: any,
  ) => {
    event.stopPropagation();
    setCardMenuAnchor(event.currentTarget);
    setSelectedProject(project);
  };

  const handleCardMenuClose = () => {
    setCardMenuAnchor(null);
    setSelectedProject(null);
  };

  const handleCopyProjectId = () => {
    if (selectedProject?._id) {
      navigator.clipboard.writeText(selectedProject._id);
      toast.success(t("Project ID copied to clipboard"));
    }
    handleCardMenuClose();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 380px)",
          minHeight: "300px",
        }}
      >
        <Box
          sx={{
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.02)",
            backdropFilter: "blur(20px)",
            border: (theme) =>
              `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)"
              }`,
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* List Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto",
              padding: "12px 24px",
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.03)"
                  : "rgba(0, 0, 0, 0.03)",
              borderBottom: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(0, 0, 0, 0.5)",
              flexShrink: 0,
            }}
          >
            <Box>{t("Project Name")}</Box>
            <Box>{t("Project Key")}</Box>
            <Box>{t("Project Type")}</Box>
            <Box>{t("Owner")}</Box>
            <Box>{t("Created At")}</Box>
            <Box sx={{ textAlign: "center", minWidth: 60 }}>{t("Actions")}</Box>
          </Box>

          {/* Scrollable List Rows */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
            }}
          >
            {isLoading || isFetching ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </Box>
            ) : (projects?.items || [])?.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <Typography>{t("No Data Found")}</Typography>
              </Box>
            ) : (
              (projects?.items || [])
                ?.filter((project: any) => {
                  if (boardTypeFilter === "all") return true;
                  return project?.boardType === boardTypeFilter;
                })
                ?.map((project: any, index: number) => {
                  const projectUrl = `/defect-tracker/projects/${project._id}/${project.boardType === "SCRUM" ? "backlog" : "task-board"}`;

                  const handleRowClick = () => {
                    if (!DEFEKT_TRACKER.VIEW(myPermissions)) return;
                    addRecentProject(project);
                    dispatch(setSelectedItem("Dashboard"));
                    navigate(projectUrl);
                  };

                  return (
                    <Box
                      key={project._id}
                      onClick={handleRowClick}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto",
                        padding: "12px 24px",
                        alignItems: "center",
                        borderBottom: (theme) =>
                          index === projects?.items?.length - 1
                            ? "none"
                            : `1px solid ${
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.05)"
                              }`,
                        cursor: DEFEKT_TRACKER.VIEW(myPermissions)
                          ? "pointer"
                          : "default",
                        transition: "all 0.3s ease",
                        "&:hover": DEFEKT_TRACKER.VIEW(myPermissions)
                          ? {
                              background: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.03)",
                            }
                          : {},
                      }}
                    >
                      {/* Project Name with Icon */}
                      <Box display="flex" alignItems="center" gap="14px">
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "8px",
                            backgroundColor: (theme) =>
                              project?.icon || theme.palette.primary.main,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: (theme) => theme.palette.common.white,
                            flexShrink: 0,
                          }}
                        >
                          {project?.name?.charAt(0)?.toUpperCase() || "P"}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 500,
                            color: (theme) => theme.palette.text.primary,
                          }}
                        >
                          {project?.name}
                        </Typography>
                      </Box>

                      {/* Project Key */}
                      <Box>
                        <Box
                          component="span"
                          sx={{
                            fontFamily: "'Monaco', 'Menlo', monospace",
                            fontSize: "13px",
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.6)"
                                : "rgba(0, 0, 0, 0.6)",
                            background: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(0, 0, 0, 0.08)",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            display: "inline-block",
                          }}
                        >
                          {project?.projectKey}
                        </Box>
                      </Box>

                      {/* Project Type */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.7)"
                                : "rgba(0, 0, 0, 0.6)",
                          }}
                        >
                          {project?.configurationType === "SUPPORT"
                            ? "Support"
                            : project?.configurationType === "IT"
                              ? "IT"
                              : "-"}
                        </Typography>
                      </Box>

                      {/* Created By */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonOutlineIcon
                          sx={{
                            fontSize: 16,
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.5)"
                                : "rgba(0, 0, 0, 0.4)",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.7)"
                                : "rgba(0, 0, 0, 0.6)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {project?.createdBy?.fullName || "-"}
                        </Typography>
                      </Box>

                      {/* Created Date */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarTodayIcon
                          sx={{
                            fontSize: 14,
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.5)"
                                : "rgba(0, 0, 0, 0.4)",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.7)"
                                : "rgba(0, 0, 0, 0.6)",
                          }}
                        >
                          {project?.createdAt
                            ? moment(project.createdAt).format(
                                dateFormat || "MMM D YYYY",
                              )
                            : "-"}
                        </Typography>
                      </Box>

                      {/* Actions */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          minWidth: 60,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleCardMenuOpen(e, project)}
                        >
                          <MoreVert sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })
            )}
          </Box>
        </Box>

        {/* Pagination - stuck at bottom */}
        <Box sx={{ flexShrink: 0, pt: 1 }}>
          <CardPagination
            currentPage={paginationModel.page}
            totalItems={projects?.totalCount || 0}
            pageSize={paginationModel.pageSize}
            onPageChange={(page) =>
              setPaginationModel((prev: any) => ({ ...prev, page }))
            }
          />
        </Box>
      </Box>

      {/* Menu for Card Actions */}
      <Menu
        anchorEl={cardMenuAnchor}
        open={Boolean(cardMenuAnchor)}
        onClose={handleCardMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {!isPmoHub && (
          <MenuItem
            onClick={() => {
              if (selectedProject?._id) {
                navigate(
                  `/defect-tracker/projects/${selectedProject._id}/applications`,
                );
              }
              handleCardMenuClose();
            }}
          >
            <ListItemIcon>
              <AppsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("Applications")}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleCopyProjectId}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("Copy Project ID")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
